// Wave 18 — First-time live-session walkthrough.
//
// Full-viewport overlay with:
//   - A darkening mask everywhere except a rounded rect cut around the
//     current step's anchor element (spotlight).
//   - A copy bubble anchored below (or above) the spotlight with title +
//     body + Next / Skip buttons and a progress dot row.
//   - Voice narration via the existing Voice service (same TTS the
//     coach panel uses; ElevenLabs with a browser-speech fallback).
//
// Anchors are DOM elements tagged with `data-onboard="stepId"`. If no
// anchor is found (e.g. the yaw prompt hasn't mounted yet, or the step
// is a "welcome" one), the bubble centres itself and the mask darkens
// the whole viewport uniformly.
//
// On complete (or skip), calls `onComplete()`. The parent hook is
// responsible for persisting `settings.hasCompletedOnboarding = true`.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { colors } from '../theme';
import { Voice } from '../services/voice';

interface Step {
  id: string;
  /** `data-onboard` value to search for. Null → centred bubble. */
  anchor: string | null;
  title: string;
  body: string;
  narration: string;
}

const STEPS: Step[] = [
  {
    id: 'welcome',
    anchor: null,
    title: 'Welcome to aero.chat',
    body: "In about 30 seconds you'll be measuring your own aerodynamics with just this camera. Let's do a quick tour.",
    narration:
      "Welcome to aero chat. In about 30 seconds you'll be measuring your own aerodynamics using just this camera. Let's walk through the basics.",
  },
  {
    id: 'camera-framing',
    anchor: 'camera-framing',
    title: 'Position yourself',
    body: 'Set up so the camera sees your side profile from about 2 metres away. Left or right side both work.',
    narration:
      'Position yourself so the camera sees your side profile from about two metres away. Either side works.',
  },
  {
    id: 'calibration',
    anchor: 'calibration-status',
    title: 'Calibration',
    body: "Watch this indicator — when it turns green we've locked pixels-per-metre. That's how we translate what the camera sees into a real CdA.",
    narration:
      "Watch the calibration indicator. When it turns green, we've locked in the scale that lets us convert what the camera sees into a real drag coefficient.",
  },
  {
    id: 'baseline',
    anchor: 'cda-tile',
    title: 'Baseline lock',
    body: 'Hold your riding position for 30 seconds. That locks a baseline CdA — every other position you try gets compared against it.',
    narration:
      "Hold your riding position steady for 30 seconds. That locks in a baseline. Every other position you try gets compared against it.",
  },
  {
    id: 'live-cda',
    anchor: 'cda-tile',
    title: 'Live CdA',
    body: 'This number is your live CdA. Lower means more aerodynamic. Try different positions and watch it react.',
    narration:
      "This number is your live CdA. Lower is more aerodynamic. Try different positions and watch how it changes.",
  },
  {
    id: 'wrap-up',
    anchor: null,
    title: "That's it",
    body: "Finish whenever — you'll get a full report at the end. You can re-run this walkthrough anytime from Account settings.",
    narration:
      "That's it. Finish whenever you like — you'll get a full report at the end. You can re-run this walkthrough anytime from account settings.",
  },
];

interface Props {
  /** Only render when true. Parent computes based on user settings. */
  active: boolean;
  /** Called when the tour completes or is skipped. Parent should
   *  persist `hasCompletedOnboarding = true`. */
  onComplete: () => void;
  /** aero-ai coach WS URL — reused for TTS. */
  wsUrl?: string;
  /** Current user id (optional; used server-side for quota labelling). */
  userId?: string;
  /** Locale for the voice — matches i18n language ('en' | 'ko'). */
  locale?: string;
}

interface AnchorRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default function OnboardingWalkthrough(props: Props) {
  const { active, onComplete, wsUrl, userId, locale } = props;
  const [stepIdx, setStepIdx] = useState(0);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);
  const voiceRef = useRef<Voice | null>(null);
  const step = STEPS[stepIdx];

  // One Voice instance per mount. Only initialised when TTS is
  // available (wsUrl present); otherwise speak() calls are silent.
  useEffect(() => {
    if (!active || !wsUrl) return;
    voiceRef.current = new Voice({
      wsUrl,
      userId,
      enabled: true,
    });
    return () => {
      voiceRef.current?.stop();
      voiceRef.current = null;
    };
  }, [active, wsUrl, userId]);

  // Narrate the current step whenever we change step.
  useEffect(() => {
    if (!active) return;
    const v = voiceRef.current;
    if (!v) return;
    v.cancelPending();
    v.speak({
      text: step.narration,
      locale,
      mode: 'default',
      trigger: `onboarding:${step.id}`,
    });
  }, [active, step, locale]);

  // Resolve the step's anchor to a viewport rect. Re-runs on step
  // change, window resize, and a short interval to catch late mounts
  // (yaw prompt, calibration strip fading in).
  useEffect(() => {
    if (!active) return;
    if (!step.anchor) {
      setAnchorRect(null);
      return;
    }
    const resolve = () => {
      const el = document.querySelector(`[data-onboard="${step.anchor}"]`) as
        | HTMLElement
        | null;
      if (!el) {
        setAnchorRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setAnchorRect({
        x: r.left - 8,
        y: r.top - 8,
        width: r.width + 16,
        height: r.height + 16,
      });
    };
    resolve();
    const id = window.setInterval(resolve, 500);
    window.addEventListener('resize', resolve);
    window.addEventListener('scroll', resolve, true);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', resolve);
      window.removeEventListener('scroll', resolve, true);
    };
  }, [active, step]);

  // Escape key skips the whole tour (matches most tour libraries).
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onComplete();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onComplete]);

  const bubblePos = useMemo(() => {
    // Centre by default; if anchor exists, place bubble below when
    // there's room, otherwise above.
    if (!anchorRect) {
      return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
    }
    const belowY = anchorRect.y + anchorRect.height + 16;
    const spaceBelow = window.innerHeight - belowY;
    if (spaceBelow >= 220) {
      return {
        left: Math.max(16, Math.min(window.innerWidth - 356, anchorRect.x)),
        top: belowY,
        transform: 'none' as const,
      };
    }
    return {
      left: Math.max(16, Math.min(window.innerWidth - 356, anchorRect.x)),
      top: Math.max(16, anchorRect.y - 220 - 16),
      transform: 'none' as const,
    };
  }, [anchorRect]);

  if (!active) return null;

  const isLast = stepIdx >= STEPS.length - 1;
  const isFirst = stepIdx === 0;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        pointerEvents: 'auto',
      }}
    >
      {/* SVG mask darkens everything except the spotlight rect (if any) */}
      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        <defs>
          <mask id="onboarding-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {anchorRect && (
              <rect
                x={anchorRect.x}
                y={anchorRect.y}
                width={anchorRect.width}
                height={anchorRect.height}
                rx={12}
                ry={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(6, 10, 20, 0.78)"
          mask="url(#onboarding-spotlight-mask)"
        />
        {anchorRect && (
          <rect
            x={anchorRect.x}
            y={anchorRect.y}
            width={anchorRect.width}
            height={anchorRect.height}
            rx={12}
            ry={12}
            fill="none"
            stroke={colors.brand}
            strokeWidth={2}
            style={{
              filter: `drop-shadow(0 0 12px ${colors.brand})`,
            }}
          />
        )}
      </svg>

      {/* Bubble */}
      <Box
        sx={{
          position: 'absolute',
          width: 340,
          maxWidth: 'calc(100vw - 32px)',
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          p: 2.5,
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          ...bubblePos,
        }}
      >
        {/* Progress dots */}
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.75 }}>
          {STEPS.map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: i === stepIdx ? colors.brand : colors.border,
              }}
            />
          ))}
        </Box>

        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: colors.text, mb: 0.75 }}>
          {step.title}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', color: colors.textMuted, mb: 2.25, lineHeight: 1.55 }}>
          {step.body}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={onComplete}
            sx={{ color: colors.textMuted, textTransform: 'none' }}
          >
            Skip tour
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isFirst && (
              <Button
                onClick={() => setStepIdx((n) => Math.max(0, n - 1))}
                sx={{
                  color: colors.text,
                  textTransform: 'none',
                  borderRadius: 2,
                }}
              >
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => {
                if (isLast) onComplete();
                else setStepIdx((n) => n + 1);
              }}
              sx={{
                backgroundColor: colors.brand,
                color: colors.textInverse,
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                px: 2.5,
              }}
            >
              {isLast ? 'Got it' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
